import * as yup from 'yup';
import { FieldErrors, useForm } from 'react-hook-form';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';

import { Dialog, MultiSelect, NumberField, Select, TextField, ToggleGroup } from 'components';
import { getFacilityName, getRoleDesignationText, inputFieldRegex } from 'utils';
import {
	getQueryError,
	handleApiResponse,
	useAddUserMutation,
	useGetLLMModelsQuery,
	useSystem,
	useUpdateUserMutation,
} from 'store';
import { useAlert, useToast } from 'context';
import { UserRole } from 'models';

import type { AddUserApiRequest, UpdateUserApiRequest } from 'store';
import type { Facility, UserInfo } from 'models';
import type { SubmitHandler } from 'react-hook-form';

const DEFAULT_MONTHLY_COST_LIMIT = 5;
const MIN_MONTHLY_COST_LIMIT = 0.01;
const MAX_MONTHLY_COST_LIMIT = 999.99;

const aiToggleOptions = [
	{ label: 'On', value: 'on' },
	{ label: 'Off', value: 'off' },
];

const getMonthlyCostLimitDefault = (monthlyCostLimit?: number | null) =>
	monthlyCostLimit == null || monthlyCostLimit < MIN_MONTHLY_COST_LIMIT ? DEFAULT_MONTHLY_COST_LIMIT : monthlyCostLimit;

const formatCurrency = (amount?: number) =>
	new Intl.NumberFormat('en-US', {
		style: 'currency',
		currency: 'USD',
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(amount ?? 0);

const getMonthlyCostUsageRatio = (costUsed?: number, monthlyCostLimit?: number) => {
	const limit = getMonthlyCostLimitDefault(monthlyCostLimit);

	if (limit <= 0) return 0;

	return Math.min(((costUsed ?? 0) / limit) * 100, 100);
};

export interface UserFormValues {
	id: number;
	firstName: string;
	lastName: string;
	email: string;
	role: UserRole;
	defaultFacility: number;
	access: string;
}

/**
 * Add/Edit User Form Structure
 */
export const UserFormSchema = yup.object({
	id: yup.number(),
	firstName: yup
		.string()
		.required('A first name is required')
		.max(100, 'First name cannot be more than 100 characters')
		.matches(inputFieldRegex, 'First Name is not valid.'),
	lastName: yup
		.string()
		.required('A last name is required')
		.max(100, 'Last name cannot be more than 100 characters')
		.matches(inputFieldRegex, 'Last Name is not valid.'),
	email: yup
		.string()
		.email('Please enter a valid email address')
		.required('An email address is required')
		.max(100, 'Email cannot be more than 100 characters'),
	role: yup.number().typeError('A role is required').min(0, 'A role is required').max(6).required('A role is required'),
	defaultFacility: yup.number().required('A default facility is required').min(1, 'A default facility is required'),
	access: yup.array().of(yup.number()).min(1, 'A facility is required'),
	hidden_healthsystems: yup.array().of(yup.number()).min(0),
	aiAccessEnabled: yup.boolean(),
	aiDashboardEnabled: yup.boolean(),
	monthlyTokenLimit: yup
		.number()
		.min(MIN_MONTHLY_COST_LIMIT, 'Monthly AI cost limit must be at least $0.01')
		.max(MAX_MONTHLY_COST_LIMIT, 'Monthly AI cost limit cannot exceed $999.99'),
	defaultModelId: yup.number().nullable(),
});

export type UserFormInputs = yup.InferType<typeof UserFormSchema>;

export interface UserSettingsDialogProps {
	children: React.ReactNode;
	user?: UserInfo;
	facilities: Facility[];
}

const roleOptions = [
	{
		label: `${getRoleDesignationText(UserRole.super_admin)}`,
		value: UserRole.super_admin,
	},
	{
		label: `${getRoleDesignationText(UserRole.admin)}`,
		value: UserRole.admin,
	},
	{
		label: `${getRoleDesignationText(UserRole.healthsystem_admin)}`,
		value: UserRole.healthsystem_admin,
	},
	{
		label: `${getRoleDesignationText(UserRole.facility_basic)}`,
		value: UserRole.facility_basic,
	},
	/* TODO: add the other role options here one day, when backend can accomodate it */
];

export function UserSettingsDialog({ children, facilities, user }: UserSettingsDialogProps) {
	const toast = useToast();
	const [shouldResetForm, setShouldResetForm] = useState(false);
	const [addUser, { isLoading: isAdding, error, isError }] = useAddUserMutation();
	const [updateUser, { isLoading: isUpdating, error: updateError, isError: isUpdateError }] = useUpdateUserMutation();
	const currentUserFacilityIds = user ? user.facilities.map((f) => f.id) : [];
	const facilityOptions = facilities.map((f) => ({
		label: getFacilityName(f),
		value: f.id,
	}));

	// keep state for the selected facilities in the access dropdown
	const [selectedFacilities, setSelectedFacilities] = useState<{ label: string; value: number }[]>(
		user ? facilityOptions.filter((f) => currentUserFacilityIds.includes(f.value)) : []
	);

	// keep state for the default facility dropdown
	const currentUserDefaultFacility = facilityOptions.find((opt) => opt.value === user?.default_facility);
	const [selectedDefault, setSelectedDefaultFacility] = useState<{ label: string; value: number } | null>(
		currentUserDefaultFacility ?? null
	);

	// keep state for role dropdown
	const currentUserRole = roleOptions.find((opt) => opt.value === user?.role);
	const [selectedRole, setSelectedRole] = useState<{ label: string; value: number } | null>(currentUserRole ?? null);

	//Applicable user roleOptions
	const { data: userData } = useSystem();
	const isSuperAdmin = userData?.user?.role === UserRole.super_admin;
	const isEditingSelf = !!user && userData?.user?.id === user.id;
	const applicableRoleOptions = userData
		? roleOptions.filter(
				(val) => val.value >= userData.user.role && (val.value !== UserRole.super_admin || (isSuperAdmin && isEditingSelf))
		  )
		: [];
	const healthsystems = userData?.healthsystems.map((h) => ({ value: h.id, label: h.name })) ?? [];
	const canManageAiAccess = userData?.user?.role === UserRole.super_admin;
	const canManageAiDashboard = canManageAiAccess || userData?.user?.id === user?.id;
	const shouldShowAiSettings = !!user && (canManageAiAccess || (canManageAiDashboard && user.ai_access_enabled));
	const { data: llmModelsData } = useGetLLMModelsQuery(undefined, { skip: !canManageAiAccess });
	const llmModelOptions = useMemo(
		() =>
			(llmModelsData?.models ?? []).map((model) => ({
				label: model.name,
				value: model.id,
			})),
		[llmModelsData?.models]
	);
	const selectedDefaultModel = useMemo(() => {
		const defaultModelId = user?.default_model_id;

		if (defaultModelId == null) return null;

		return (
			llmModelOptions.find((model) => Number(model.value) === Number(defaultModelId)) ??
			(user?.default_model ? { label: user.default_model.name, value: user.default_model.id } : null)
		);
	}, [llmModelOptions, user?.default_model, user?.default_model_id]);
	const [selectedModel, setSelectedModel] = useState<{ label: string; value: number } | null>(selectedDefaultModel);

	// set up all of the form logic/handlers
	const {
		register,
		handleSubmit,
		reset,
		setValue,
		getValues,
		watch,
		formState: { errors },
	} = useForm<UserFormInputs>({
		resolver: yupResolver(UserFormSchema),
		mode: 'onChange',
		defaultValues: {
			id: user?.id,
			firstName: user?.first_name ?? '',
			lastName: user?.last_name ?? '',
			email: user?.email ?? '',
			role: user?.role ?? -1,
			defaultFacility: user?.default_facility, // no facility will have this id
			access: user?.facilities.map((f) => f.id) ?? [],

			// use the id to find the healthsystem from healthsystems array
			hidden_healthsystems: user?.hidden_healthsystems ?? [],
			aiAccessEnabled: user?.ai_access_enabled ?? false,
			aiDashboardEnabled: user?.ai_dashboard_enabled ?? false,
			monthlyTokenLimit: getMonthlyCostLimitDefault(user?.monthly_token_limit),
			defaultModelId: user?.default_model_id ?? null,
		},
	});
	const { getAlertResponse } = useAlert();
	const [dialogOpen, setDialogOpen] = useState(false);
	const aiAccessEnabled = watch('aiAccessEnabled');
	const aiDashboardEnabled = watch('aiDashboardEnabled');
	const monthlyCostLimitValue = watch('monthlyTokenLimit');
	const costUsedInPeriod = user?.tokens_used_in_period ?? 0;
	const monthlyCostLimit = getMonthlyCostLimitDefault(user?.monthly_token_limit);
	const monthlyCostUsageRatio = getMonthlyCostUsageRatio(costUsedInPeriod, monthlyCostLimit);
	const monthlyCostUsageRatioLabel = `${monthlyCostUsageRatio.toFixed(monthlyCostUsageRatio >= 10 ? 0 : 1)}%`;
	const aiPacingLimitPercent = user?.ai_pacing_limit_percent;
	const aiPacingLimitLabel =
		typeof aiPacingLimitPercent === 'number'
			? `${aiPacingLimitPercent.toFixed(aiPacingLimitPercent >= 10 ? 0 : 1)}%`
			: null;
	const aiAccessToggleValue = aiAccessEnabled ? 'on' : 'off';
	const aiDashboardToggleValue = aiDashboardEnabled ? 'on' : 'off';

	const onSubmit: SubmitHandler<UserFormInputs> = async (formData) => {
		// first, build the request by appending the id if editing an existing user
		const facilityIds = formData.access ? formData.access.map((v) => v) : [];
		const hidden_healthsystems = formData.hidden_healthsystems?.flatMap((v) => (v !== undefined ? [v] : []));
		const requestBody: AddUserApiRequest = {
			first_name: formData.firstName,
			last_name: formData.lastName,
			email: formData.email,
			has_ai_tokens: false,
			role: formData.role,
			default_facility: formData.defaultFacility,
			facility_access: facilityIds,
			hidden_healthsystems: hidden_healthsystems ?? [],
		};

		// bundle the calls into an if-else
		let response;
		if (formData.id) {
			const aiAccessRequested = formData.aiAccessEnabled ?? false;

			// Partial<UpdateUserApiRequest> means this object can contain any subset of
			// UpdateUserApiRequest fields. We build it gradually because each AI setting
			// has different permission rules, and not every update should send every field.
			const aiSettings: Partial<UpdateUserApiRequest> = {};

			// Only super admins can grant or revoke AI access for users.
			if (canManageAiAccess) {
				aiSettings.ai_access_enabled = aiAccessRequested;
			}

			// In this admin flow, dashboard visibility follows AI access:
			// enabling AI access enables the dashboard, and disabling access hides it.
			if (canManageAiDashboard) {
				aiSettings.ai_dashboard_enabled = aiAccessRequested;
			}

			// Monthly AI cost limits only apply while AI access is enabled.
			if (canManageAiAccess && aiAccessRequested) {
				aiSettings.monthly_token_limit = formData.monthlyTokenLimit ?? DEFAULT_MONTHLY_COST_LIMIT;
			}

			// Clear the default model when AI access is disabled.
			if (canManageAiAccess) {
				aiSettings.default_model_id = aiAccessRequested ? formData.defaultModelId ?? null : null;
			}

			response = await updateUser({
				id: formData.id,
				...requestBody,
				...aiSettings,
			});
		} else {
			response = await addUser(requestBody);
		}

		// handle either type of response (edit / add)
		handleApiResponse(response, {
			success: (payload) => {
				if (!user) setShouldResetForm(true);
				toast.createToast({
					title: user
						? `${payload.user.first_name} ${payload.user.last_name} has been successfully updated.`
						: `${payload.user.first_name} ${payload.user.last_name} has been successfully created.`,
					variant: 'success',
				});
				setDialogOpen(false);
			},
			error: (payload) => {
				toast.createToast({
					title: getQueryError(payload),
					variant: 'error',
				});
			},
		});
	};

	// on every re-render, recreate the function that resets the form
	const resetForm = useCallback(() => {
		setValue('role', -1, { shouldValidate: true });
		setValue('defaultFacility', 0);
		setValue('access', []);
		setSelectedFacilities([]);
		setSelectedDefaultFacility(null);
		setSelectedRole(null);
		setSelectedModel(null);
		reset();
	}, [reset, setSelectedDefaultFacility, setSelectedFacilities, setSelectedRole, setValue]);

	useEffect(() => {
		setSelectedModel(selectedDefaultModel);

		if (selectedDefaultModel) {
			setValue('defaultModelId', selectedDefaultModel.value, { shouldValidate: true });
		}
	}, [selectedDefaultModel, setValue]);

	useEffect(() => {
		// if at any point our component just submitted a new user, on the next render lets reset our form
		if (shouldResetForm) {
			resetForm();
			setShouldResetForm(false);
		}
	}, [resetForm, setShouldResetForm, shouldResetForm]);

	useEffect(() => {
		if (!aiAccessEnabled) {
			// Dashboard visibility only makes sense when AI access is enabled,
			// so force it off any time AI access is turned off.
			if (getValues('aiDashboardEnabled') !== false) {
				setValue('aiDashboardEnabled', false, { shouldValidate: true });
			}

			// A default model should not be saved for users without AI access.
			if (getValues('defaultModelId') !== null) {
				setValue('defaultModelId', null, { shouldValidate: true });
			}

			// Keep the Select component's local state in sync with the form value above.
			setSelectedModel(null);
		} else {
			// When AI access is enabled, make sure the form has a usable token limit.
			const monthlyCostLimit = getValues('monthlyTokenLimit');

			if (monthlyCostLimit === undefined || monthlyCostLimit === null || monthlyCostLimit < MIN_MONTHLY_COST_LIMIT) {
				setValue('monthlyTokenLimit', DEFAULT_MONTHLY_COST_LIMIT, { shouldValidate: true });
			}

			// If this user does not already have a default model, choose the last model
			// option as the default for newly enabled AI access.
			if (!user?.default_model_id && !getValues('defaultModelId') && llmModelOptions.length > 0) {
				const defaultModel = llmModelOptions[llmModelOptions.length - 1];

				setValue('defaultModelId', defaultModel.value, { shouldValidate: true });
				setSelectedModel(defaultModel);
			}
		}
	}, [aiAccessEnabled, getValues, llmModelOptions, setValue, user?.default_model_id]);

	return (
		<Dialog
			actionButtons={[
				{
					children: `${user ? 'Update' : 'Add New'} User`,
					isWorking: isAdding || isUpdating,
					onClick: handleSubmit(onSubmit),
				},
			]}
			sizeX='lg'
			title={`${user ? 'Edit' : 'Add New'} User`}
			trigger={children}
			open={dialogOpen}
			onOpenChange={async (isBeingOpened) => {
				if (isBeingOpened) {
					setDialogOpen(true);
					return;
				} else if (user) {
					setDialogOpen(false);
					return;
				}

				const alertResponse = await getAlertResponse({
					description:
						"Closing the window will clear any of the information you've entered in the form, are you sure you'd like to continue?",
					responseOptions: [
						{
							value: 'yes',
							label: 'Yes, close',
						},
					],
				});

				if (alertResponse === 'yes') {
					resetForm();
					setDialogOpen(false);
				}
			}}
		>
			<form
				className='flex flex-col max-h-[calc(100vh-220px)] overflow-y-auto'
				onSubmit={handleSubmit(onSubmit)}
				noValidate
				autoComplete='off'
			>
				<div className='grid grid-cols-2 gap-10 py-4 px-8'>
					<div>
						<TextField
							disabled={!!user}
							label='First Name'
							type='text'
							sizeY='md'
							errorMessage={errors.firstName?.message}
							{...register('firstName')}
						/>
					</div>

					<div>
						<TextField
							disabled={!!user}
							label='Last Name'
							type='text'
							sizeY='md'
							errorMessage={errors.lastName?.message}
							{...register('lastName')}
						/>
					</div>

					<div>
						<TextField
							disabled={!!user}
							label='Email'
							type='email'
							sizeY='md'
							errorMessage={errors.email?.message}
							{...register('email')}
						/>
					</div>

					<div>
						<Select
							label='Access Level (Role)'
							sizeY='md'
							errorMessage={errors.role?.message}
							options={applicableRoleOptions}
							value={selectedRole}
							onChange={(option) => {
								if (option) {
									setValue('role', option.value, { shouldValidate: true });
									setSelectedRole(option);
								}
							}}
						/>
					</div>

					<div>
						<MultiSelect
							label='Facilities'
							sizeY='md'
							errorMessage={(errors.access as FieldErrors)?.message}
							options={facilityOptions}
							value={selectedFacilities}
							onChange={(selected) => {
								// since we're updating the facilities the user has access to, reset the default facility dropdown field
								setSelectedFacilities(
									selected.map((option) => ({
										label: option.label,
										value: option.value,
									}))
								);
								setSelectedDefaultFacility(null);

								// make the same updates to our react-hook state above to the underlying form state as well
								setValue('defaultFacility', 0, { shouldValidate: true });
								setValue(
									'access',
									selected.map((option) => option.value),
									{ shouldValidate: true }
								);
							}}
						/>
					</div>

					<div>
						<Select
							label='Default Facility'
							sizeY='md'
							errorMessage={errors.defaultFacility?.message}
							options={selectedFacilities}
							value={selectedDefault}
							onChange={(option) => {
								if (option) {
									// update the react-hook state and the react-hook-form state for this field
									setSelectedDefaultFacility(option);
									setValue('defaultFacility', option.value, { shouldValidate: true });
								}
							}}
						/>
					</div>
					<div>
						{getValues('role') === UserRole.admin && (
							<MultiSelect
								label='Hidden Health Systems'
								sizeY='md'
								errorMessage={errors.defaultFacility?.message}
								options={healthsystems}
								value={healthsystems
									.filter((h) => getValues('hidden_healthsystems')?.includes(h.value))
									.map((option) => ({
										label: option.label,
										value: option.value,
									}))}
								onChange={(option) => {
									if (option) {
										// update the react-hook state and the react-hook-form state for this field
										// setSelectedDefaultFacility(option);
										setValue(
											'hidden_healthsystems',
											option.map((o) => o.value),

											{ shouldValidate: true }
										);
									}
								}}
							/>
						)}
					</div>
					<div className='flex flex-col justify-around text-center align-middle mt-2'>
						{(user?.hidden_healthsystems ?? []).length > 0 && userData?.user.id === user?.id && (
							<div className='text-yellow-500 text-p2'>{`${
								(user?.hidden_healthsystems ?? []).length
							} health system(s) are hidden for you.`}</div>
						)}
					</div>
					{shouldShowAiSettings && (
						<div className='col-span-2 border-t border-bluegray-100 pt-6'>
							<div>
								<div className='mb-5 flex items-start justify-between gap-4'>
									<div>
										<p className='text-p2 font-semibold text-bluegray-700'>AI Settings</p>
										<p className='mt-1 text-p3 text-bluegray-500'>Manage AI access, dashboard visibility, and usage limits.</p>
									</div>
								</div>

								<div className='grid grid-cols-2 gap-10'>
									{canManageAiAccess && (
										<div>
											<div className='mb-3'>
												<p className='text-p3 font-semibold text-bluegray-700'>Allow AI Access</p>
												<p className='mt-1 text-p3 text-bluegray-500'>Enable this user to access Merlin AI features.</p>
											</div>
											<ToggleGroup
												key={`ai-access-${aiAccessToggleValue}`}
												label='Allow AI access'
												hideLabel
												className='w-full'
												options={aiToggleOptions}
												defaultValue={aiAccessToggleValue}
												onValueChange={(selected) =>
													setValue('aiAccessEnabled', selected === 'on', { shouldDirty: true, shouldValidate: true })
												}
											/>
										</div>
									)}

									{aiAccessEnabled && !canManageAiAccess && (
										<div>
											<div className='mb-3'>
												<p className='text-p3 font-semibold text-bluegray-700'>Show AI Dashboard</p>
												<p className='mt-1 text-p3 text-bluegray-500'>
													Control whether this dashboard is visible for your account.
												</p>
											</div>
											<ToggleGroup
												key={`ai-dashboard-${aiDashboardToggleValue}`}
												label='Show AI dashboard'
												hideLabel
												className='w-full'
												options={aiToggleOptions}
												defaultValue={aiDashboardToggleValue}
												onValueChange={(selected) =>
													setValue('aiDashboardEnabled', selected === 'on', { shouldDirty: true, shouldValidate: true })
												}
											/>
										</div>
									)}

									{aiAccessEnabled && canManageAiDashboard && (
										<div className='col-span-2 rounded bg-bluegray-50 p-4'>
											<div className='flex items-center justify-between gap-4'>
												<div>
													<p className='text-p3 font-semibold text-bluegray-700'>Monthly AI Usage</p>
													<p className='mt-1 text-p3 text-bluegray-500'>
														{formatCurrency(costUsedInPeriod)} of {formatCurrency(monthlyCostLimit)} used
													</p>
												</div>
												<p className='text-p2 font-semibold text-bluegray-700'>{monthlyCostUsageRatioLabel}</p>
											</div>
											<div className='relative mt-3 h-2 overflow-hidden rounded-full bg-white'>
												<div
													className='h-full rounded-full bg-blue-500 transition-all duration-300'
													style={{ width: `${monthlyCostUsageRatio}%` }}
												/>
												{typeof aiPacingLimitPercent === 'number' && (
													<span
														aria-hidden='true'
														className='pointer-events-none absolute inset-y-0 -translate-x-1/2 border-l-2 border-dashed border-red-400'
														style={{ left: `${aiPacingLimitPercent}%` }}
													/>
												)}
											</div>
											{aiPacingLimitLabel && (
												<div className='mt-2 flex items-center gap-2 text-p3 text-bluegray-500'>
													<span className='w-5 border-t-2 border-dashed border-red-400' />
													<span>Usage limit starts at {aiPacingLimitLabel} usage today</span>
												</div>
											)}
										</div>
									)}

									{aiAccessEnabled && canManageAiAccess && (
										<>
											<div>
												<NumberField
													label='Monthly AI Cost Limit'
													min={MIN_MONTHLY_COST_LIMIT}
													max={MAX_MONTHLY_COST_LIMIT}
													step={0.01}
													sizeY='md'
													value={monthlyCostLimitValue ?? getMonthlyCostLimitDefault(user.monthly_token_limit)}
													defaultValue={getMonthlyCostLimitDefault(user.monthly_token_limit)}
													showRangeInput={false}
													errorMessage={errors.monthlyTokenLimit?.message}
													{...register('monthlyTokenLimit')}
												/>
											</div>
											<div>
												<Select
													label='Default AI Model'
													sizeY='md'
													value={selectedModel}
													options={llmModelOptions}
													onChange={(option) => {
														if (option) {
															setSelectedModel(option);
															setValue('defaultModelId', option.value, { shouldValidate: true });
														}
													}}
												/>
											</div>
										</>
									)}

									{!aiAccessEnabled && canManageAiDashboard && (
										<div className='col-span-2 rounded bg-bluegray-50 p-4 text-p3 text-bluegray-500'>
											Enable AI access to configure dashboard visibility, monthly token limits, and default model selection.
										</div>
									)}
								</div>
							</div>
						</div>
					)}
				</div>
				{isUpdateError && updateError && <p className='my-2 text-p2 text-red-400'>{getQueryError(updateError)}</p>}
				{isError && error && <p className='my-2 text-p2 text-red-400'>{getQueryError(error)}</p>}
			</form>
		</Dialog>
	);
}

export default UserSettingsDialog;
